/**
 * Global Cmd+K command-bar overlay.
 *
 * Mount this once near the top of the App tree (alongside the toast
 * provider). It owns:
 *
 *   * The `open` state for the modal flavor of the command bar.
 *   * The Cmd+K / Ctrl+K keybinding (ignored when the user is typing
 *     inside another input — we don't want to hijack form fields).
 *   * The fixed-position overlay that hosts <CommandBar variant="modal" />.
 *
 * The dashboard's inline command bar is independent — same component,
 * different mount, no shared state. Each conversation_id is per-mount,
 * so opening Cmd+K starts a fresh thread.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

import { CommandBar } from "./CommandBar";


interface CommandBarContextValue {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
}

const CommandBarContext = createContext<CommandBarContextValue | null>(null);


function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function CommandBarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Cmd+K (Mac) / Ctrl+K (Windows). Skipped when the user is typing
  // somewhere — the input itself can implement Cmd+K if it wants to.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isAccel = e.metaKey || e.ctrlKey;
      if (!isAccel) return;
      if (e.key !== "k" && e.key !== "K") return;
      // Ctrl+K inside a normal field still works on focus loss but we
      // intercept it cleanly so nothing else fires.
      if (isTypingTarget(e.target) && !isOpen) {
        // Allow Cmd+K to open even while focused in a generic input —
        // power users expect that. But suppress when the focused input
        // is OUR command-bar input (avoid double-toggle).
        const t = e.target as HTMLElement;
        if (t.dataset.commandBarInput === "true") {
          // Let the input handle its own Escape.
          return;
        }
      }
      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, toggle]);

  // Esc to close (independent of where focus is).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  const value = useMemo<CommandBarContextValue>(
    () => ({ open, close, toggle, isOpen }),
    [open, close, toggle, isOpen],
  );

  return (
    <CommandBarContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm pt-24 px-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 280 }}
              className="w-full max-w-2xl"
            >
              <CommandBar
                variant="modal"
                autoFocus
                onClose={close}
                // Override `data-` so the modal input is excluded from
                // re-toggling — the global handler reads this attr.
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CommandBarContext.Provider>
  );
}


export function useCommandBar(): CommandBarContextValue {
  const ctx = useContext(CommandBarContext);
  if (!ctx) {
    throw new Error(
      "useCommandBar must be used inside <CommandBarProvider>.",
    );
  }
  return ctx;
}
